import { useCallback, useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Clock } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { useAuth } from "@/context/AuthContext";
import { useIdleLogout } from "@/hooks/useIdleLogout";
import api from "@/lib/api";

export default function IdleGuard({ children }) {
  const { logout } = useAuth();
  const nav = useNavigate();
  const { pathname } = useLocation();
  const [minutes, setMinutes] = useState(15);
  const [kioskExempt, setKioskExempt] = useState(true);

  useEffect(() => {
    api.get("/compliance/me").then((r) => {
      if (r.data?.idle_minutes) setMinutes(r.data.idle_minutes);
      setKioskExempt(r.data?.kiosk_never_idle !== false);
    }).catch(() => {});
  }, []);

  const onKiosk = pathname === "/kiosk" || pathname.startsWith("/kiosk/");
  const enabled = !(onKiosk && kioskExempt);

  // Kiosk stations stay open for days — keep the access token fresh so it never expires.
  useEffect(() => {
    if (enabled) return undefined;
    const refresh = () => api.post("/auth/refresh")
      .then((r) => { if (r.data?.token) localStorage.setItem("vlog_token", r.data.token); })
      .catch(() => {});
    refresh();
    const iv = setInterval(refresh, 60 * 60 * 1000);
    return () => clearInterval(iv);
  }, [enabled]);

  const onTimeout = useCallback(async () => {
    await logout();
    nav("/login?idle=1", { replace: true });
  }, [logout, nav]);

  const { warning, secondsLeft, stay } = useIdleLogout({ minutes, onTimeout, enabled });

  return (
    <>
      {children}
      <Dialog open={warning} onOpenChange={(o) => { if (!o) stay(); }}>
        <DialogContent className="max-w-sm" data-testid="idle-warning-dialog">
          <DialogHeader>
            <DialogTitle className="font-display flex items-center gap-2"><Clock className="h-5 w-5 text-amber-600" /> Still there?</DialogTitle>
            <DialogDescription>
              For privacy, you'll be signed out automatically in <b data-testid="idle-seconds-left">{secondsLeft}</b> second{secondsLeft === 1 ? "" : "s"} of inactivity.
            </DialogDescription>
          </DialogHeader>
          <div className="flex justify-end gap-2 pt-2">
            <button type="button" onClick={onTimeout} data-testid="idle-signout-now" className="h-10 rounded-lg border border-slate-200 px-4 text-sm font-medium text-slate-700 hover:bg-slate-50">Sign out now</button>
            <button type="button" onClick={stay} data-testid="idle-stay-btn" className="h-10 rounded-lg bg-primary px-4 text-sm font-semibold text-white">Stay signed in</button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
